import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconBuilding, IconCalendar, IconEuro, IconDelete, IconSuccess } from '../../icons';
import { ReceivedInvoice } from '../../types/receivedInvoiceTypes';
import { useIsReadOnly } from '../../hooks/useIsReadOnly';
import MessageContextButton from '../messaging/MessageContextButton';
import { formatDate as formatDateCentral } from '../../utils/formatters';

interface ReceivedInvoiceCardProps {
  invoice: ReceivedInvoice;
  onClick: (invoice: ReceivedInvoice) => void;
  /** Delete handler. Optional: when omitted (Loop-A `INBOUND_ENABLED` on) the
   *  delete affordance is not rendered at all (received_invoices delete: if false). */
  onDelete?: (id: string) => void;
  /**
   * When provided (feature flag REACT_APP_EXPENSES_ENABLED active), shows a
   * "Als Ausgabe übernehmen" button. If isExpensed is true the button is
   * replaced with an "Bereits übernommen" badge.
   */
  onBookExpense?: (invoice: ReceivedInvoice) => void;
  /** Whether this invoice already has an associated expense (dedup guard). */
  isExpensed?: boolean;
}

const ReceivedInvoiceCard: React.FC<ReceivedInvoiceCardProps> = ({
  invoice,
  onClick,
  onDelete,
  onBookExpense,
  isExpensed,
}) => {
  const { t, i18n } = useTranslation('common');
  const { t: tExpenses } = useTranslation('expenses');
  // M11b-3: advisor (read-only) sieht keine Löschen-Aktion (Buchen wird vom
  // Aufrufer via fehlendem onBookExpense unterdrückt). Karte selbst bleibt lesbar.
  const isReadOnly = useIsReadOnly();
  const { data } = invoice;
  const [showConfirm, setShowConfirm] = useState(false);

  const dueDateStatus = (): 'overdue' | 'soon' | 'ok' | null => {
    if (!data.dueDate) return null;
    const due = new Date(data.dueDate);
    const now = new Date();
    const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'overdue';
    if (diffDays <= 7) return 'soon';
    return 'ok';
  };

  const status = dueDateStatus();

  const dueDateColor = {
    overdue: 'text-[var(--tone-danger-fg)]',
    soon: 'text-[var(--tone-warning-fg)]',
    ok: 'text-[var(--tone-success-fg)]',
    null: 'text-muted',
  }[status ?? 'null'];

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '–';
    return formatDateCentral(dateStr, { locale: i18n.language });
  };

  const formatAmount = (amount: number, currency: string) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(amount);

  return (
    <div className="relative">
    <div
      onClick={() => !showConfirm && onClick(invoice)}
      className="bg-surface-raised border border-hairline hover:border-indigo-500 rounded-xl p-5 cursor-pointer transition-all hover:shadow-lg hover:shadow-indigo-900/20 group"
    >
      {/* Header row */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-accent-ink font-mono mb-1 truncate">{data.documentNo || '–'}</p>
          <h3 className="text-primary font-semibold text-sm truncate group-hover:text-accent-ink transition-colors">
            {data.sellerName || t('receivedInvoices.unknownSeller', 'Unbekannter Absender')}
          </h3>
        </div>
        <span className="ml-3 text-base font-bold text-primary whitespace-nowrap">
          {formatAmount(data.grandTotal, data.currency)}
        </span>
      </div>

      {/* Detail rows */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 text-xs text-muted">
          <IconCalendar size={12} className="flex-shrink-0" />
          <span>{t('receivedInvoices.invoiceDate', 'Datum')}: {formatDate(data.documentDate)}</span>
        </div>

        {data.dueDate && (
          <div className={`flex items-center gap-2 text-xs ${dueDateColor}`}>
            <IconEuro size={12} className="flex-shrink-0" />
            <span>
              {t('receivedInvoices.dueDate', 'Fällig')}: {formatDate(data.dueDate)}
              {status === 'overdue' && (
                <span className="ml-1 text-[var(--tone-danger-fg)]">
                  ({t('receivedInvoices.overdue', 'Überfällig')})
                </span>
              )}
            </span>
          </div>
        )}

        {data.sellerCity && (
          <div className="flex items-center gap-2 text-xs text-faint">
            <IconBuilding size={12} className="flex-shrink-0" />
            <span className="truncate">{[data.sellerZip, data.sellerCity].filter(Boolean).join(' ')}</span>
          </div>
        )}
      </div>

      {/* Net/tax footer */}
      <div className="mt-3 pt-3 border-t border-hairline flex justify-between items-center text-xs text-faint">
        <span>{t('receivedInvoices.totalNet', 'Netto')}: {formatAmount(data.netTotal, data.currency)}</span>
        <div className="flex items-center gap-3">
          <span>{t('receivedInvoices.totalTax', 'MwSt.')}: {formatAmount(data.taxTotal, data.currency)}</span>
          {/* M-MSG-3: Nachrichten-Thread zum Beleg — additiv, sichtbar für BEIDE Rollen
              (auch advisor read-only), nur hinter canUseMessaging(effectiveScopeUid).
              stopPropagation, da der Klick sonst die Karte öffnen würde (onClick auf
              dem äußeren div). */}
          <span onClick={(e) => e.stopPropagation()}>
            <MessageContextButton
              context={{ type: 'receipt', refId: invoice.id }}
              refCollection="received_invoices"
              contextSummary={{
                title: data.documentNo || invoice.id,
                subtitle: [data.sellerName, formatDate(data.documentDate)].filter(Boolean).join(' · '),
                amountLabel: formatAmount(data.grandTotal, data.currency),
                badgeVariant: 'receipt',
                hrefToRef: '/dashboard/eingangsrechnungen',
              }}
            />
          </span>
          {/* Löschen — M11b-3: für advisor (read-only) ausgeblendet;
              Loop-A: bei INBOUND_ENABLED (onDelete omitted) ganz entfernt */}
          {!isReadOnly && onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); setShowConfirm(true); }}
            className="text-gray-600 hover:text-[var(--tone-danger-fg)] transition-colors p-1 -mr-1"
            title={t('receivedInvoices.delete', 'Rechnung entfernen')}
          >
            <IconDelete size={12} />
          </button>
          )}
        </div>
      </div>

      {/* Expense booking action — only rendered when feature flag is active */}
      {onBookExpense && (
        <div className="mt-2 pt-2 border-t border-hairline">
          {isExpensed ? (
            <div className="flex items-center gap-1.5 text-xs text-[var(--tone-success-fg)]">
              <IconSuccess size={12} className="flex-shrink-0" />
              <span title={tExpenses('alreadyBookedHint')}>{tExpenses('alreadyBooked')}</span>
            </div>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onBookExpense(invoice);
              }}
              className="w-full text-xs text-accent-ink hover:text-accent-ink hover:bg-[var(--tone-accent-bg)] rounded-lg py-1.5 px-2 transition-colors text-left"
            >
              + {tExpenses('bookFromInvoice')}
            </button>
          )}
        </div>
      )}
    </div>

    {/* Delete confirmation overlay */}
    {showConfirm && (
      <div
        className="absolute inset-0 bg-[rgb(var(--surface-base-rgb)/0.9)] rounded-xl flex flex-col items-center justify-center gap-3 z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-primary font-medium text-sm">
          {t('receivedInvoices.deleteConfirm', 'Rechnung entfernen?')}
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => onDelete?.(invoice.id)}
            className="bg-red-600 hover:bg-red-500 text-white text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors"
          >
            {t('receivedInvoices.deleteYes', 'Ja, entfernen')}
          </button>
          <button
            onClick={() => setShowConfirm(false)}
            className="bg-surface-inset hover:bg-surface-hover text-primary text-xs px-3 py-1.5 rounded-lg transition-colors"
          >
            {t('cancel', 'Abbrechen')}
          </button>
        </div>
      </div>
    )}
    </div>
  );
};

export default ReceivedInvoiceCard;
